import { Injectable } from "@angular/core";
import { EDGE_TYPE, FILTER_MODEL_KEY, LAYOUTS, Linqset, LinqsetEdge, LinqsetNode, MODEL_TYPE, NODE_SUPERTYPE, NODE_TYPE, ORGANISATION_MODEL_KEY, ORGANISATION_VIEW_MODEL_TYPES, PEOPLE_SYSTEMS_LAYOUTS, PEOPLE_SYSTEMS_VIEWS, getEdgeDescription, getInsights, isOrganisationEdge, isOrganisationNode, isRolePeople, isRoleSystem, isSupplyChain, isSystemInfrastructure } from "../common/linqset-model";
import { EdgeService } from "./edge.service";
import { NodeService } from "./node.service";
import { v4 } from "uuid";
import { Organisation } from "../common/organisation";
import { OrganisationService } from "../organisation/organisation.service";
import { LinqsetRepositoryService } from "./linqset-repository.service";
import { createModelEdge, LinqsetNodeVisualisation } from "../canvas/model/graph-model";
import { getStartingNodes } from "../common/model-processing";
import { AdjacencyTypes, Color, GraphComponent, GraphModelManager, IColumn, IEdge, IGraph, INode, IRow, ITable, LabelStyle, NodeStyleStripeStyleAdapter, ShapeNodeStyle, Size, StretchStripeLabelModel, Table, TableNodeStyle, TableRenderingOrder } from "@yfiles/yfiles";
import { LabelService } from "./label.service";
import { UserService } from "../user/user.service";
import { GraphService } from "../canvas/graph.service";
import { BuildModelService } from "./build-model.service";
import { LAYOUT_DIRECTION } from "../common/user";

// the "used by" edge from a system to the role one of a role's edges comes from. A role appears once for each place
// it is used, and each appearance has its own system; edges without visualisation keys can't be matched
const findUsedBy = (linqset:Linqset, roleEdge:LinqsetEdge) => roleEdge.sourceVisualisationKey
    ? linqset.edges.find(e => e.type === EDGE_TYPE.USEBY && e.targetVisualisationKey === roleEdge.sourceVisualisationKey)
    : undefined

@Injectable()
export class LinqModelToCanvasService {

    constructor(private nodeService: NodeService, private edgeService: EdgeService, private organisationService:OrganisationService,
        private repoService:LinqsetRepositoryService, private labelService:LabelService, private userService:UserService, private graphService:GraphService) {
    }

    public getModelTypes(linqset:Linqset):MODEL_TYPE[] {
        if (!linqset) return []

        return Object.values(MODEL_TYPE).filter(t => {
            const v = ORGANISATION_VIEW_MODEL_TYPES.indexOf(t)
            return linqset.hasModelNodes?v<0:v>=0
        })
    }

    // the tag path layout needs a tag, so it's chosen from the tags of the tag group shown instead. The main path is of
    // the work in a model, so the organisation's views of its people, systems and models have none
    public getLayouts(modelType:MODEL_TYPE):LAYOUTS[] {
        return Object.values(LAYOUTS).filter(l => l !== LAYOUTS.HIERARCHIC_TAG && (l !== LAYOUTS.HIERARCHIC_MAIN || !ORGANISATION_VIEW_MODEL_TYPES.includes(modelType)))
    }

    public async createGraph(organisation:Organisation, graphComponent: GraphComponent, linqset:Linqset, modelType:MODEL_TYPE) {
        let nodes:LinqsetNode[] = undefined
        graphComponent.graph.clear()
        graphComponent.selection.clear()
        graphComponent.canvasContext.canvasComponent.updateVisual()

        graphComponent.graph.tag = {orientation:this.userService.getUserPreferences().graphDirection}

        linqset.modelType = modelType

        // people and systems have no flow, so a hierarchic layout puts them in one long row; organic groups
        // each role with the systems it uses, unless the user last chose another for them. A layout picked from the
        // toolbar is kept
        if (!this.graphService.chosenLayouts.has(linqset)) {
            const chosen = this.userService.getUserPreferences()?.peopleSystemsLayout as LAYOUTS | undefined
            linqset.layout = PEOPLE_SYSTEMS_VIEWS.includes(modelType) ? (chosen && PEOPLE_SYSTEMS_LAYOUTS.includes(chosen) ? chosen : LAYOUTS.ORGANIC) : LAYOUTS.HIERARCHIC
        }

        graphComponent.graphModelManager = new GraphModelManager()

        switch (modelType) {
            //case MODEL_TYPE.COLLAPSED:
            //    linqset.layout = LAYOUTS.HIERARCHIC
            //    await this.buildModelService.createCollapsedModel(organisation, linqset.filter, graphComponent)
            //    return
            case MODEL_TYPE.SIPOC:
                graphComponent.graph.tag.orientation = LAYOUT_DIRECTION.LEFT_RIGHT
                this.createSipoc(organisation, linqset, graphComponent)
                break
            case MODEL_TYPE.SWIMLANES_ROLE:
            case MODEL_TYPE.SWIMLANES_SYSTEM:
                nodes = linqset.nodes.filter(n => isSupplyChain(n.superType))
                this.createStandardProcessGraph(graphComponent, linqset, nodes)
                this.createSwimlaneGraph(linqset, graphComponent, modelType===MODEL_TYPE.SWIMLANES_ROLE?NODE_SUPERTYPE.ROLE:NODE_SUPERTYPE.SYSTEM)
                break
            case MODEL_TYPE.SWIMLANES_ROLE_ACTIONS:
            case MODEL_TYPE.SWIMLANES_SYSTEM_ACTIONS:
                nodes = linqset.nodes.filter(n => n.superType===NODE_SUPERTYPE.ACTION)
                this.createSingleTypeGraph(organisation, linqset, graphComponent, NODE_SUPERTYPE.ACTION)
                this.createSwimlaneGraph(linqset, graphComponent, modelType===MODEL_TYPE.SWIMLANES_ROLE_ACTIONS?NODE_SUPERTYPE.ROLE:NODE_SUPERTYPE.SYSTEM)
                break
            case MODEL_TYPE.SUPPLY_CHAIN:
                nodes = linqset.nodes.filter(n => isSupplyChain(n.superType))
                this.createStandardProcessGraph(graphComponent, linqset, nodes)
                break
            case MODEL_TYPE.PROCESS:
                this.createStandardProcessGraph(graphComponent, linqset, nodes)
                break
            case MODEL_TYPE.ACTIONS:
                this.createSingleTypeGraph(organisation, linqset, graphComponent, NODE_SUPERTYPE.ACTION)
                break
            case MODEL_TYPE.INFORMATION:
                this.createSingleTypeGraph(organisation, linqset, graphComponent, NODE_SUPERTYPE.INFORMATION)
                break
            case MODEL_TYPE.INFORMATION_ASSETS:
                this.createInformationAssetGraph(organisation, linqset, graphComponent)
                break
            case MODEL_TYPE.SYSTEM_INTERFACES:
                this.createSystemInterfacesGraph(linqset, graphComponent)
                break
            case MODEL_TYPE.ROLE_HANDOVERS:
                this.createRoleInterfacesGraph(linqset, graphComponent)
                break
            case MODEL_TYPE.MODELS_CONNECTION:
                nodes = linqset.nodes.filter(n => n.type !== NODE_TYPE.CAPTURE && (n.superType === NODE_SUPERTYPE.ACTION || (n.superType === NODE_SUPERTYPE.INFORMATION && n.external)))
                this.createGraphWithoutVisualisations(graphComponent, linqset, modelType, nodes)
                break
            case MODEL_TYPE.MODELS:
                this.simplifiedModels(graphComponent, linqset)
                break
            case MODEL_TYPE.PEOPLE:
                nodes = linqset.nodes.filter(n => isRolePeople(n.superType))
                this.createGraphWithoutVisualisations(graphComponent, linqset, modelType, nodes)
                break
            case MODEL_TYPE.PEOPLE_AND_SYSTEMS:
                nodes = linqset.nodes.filter(n => isOrganisationNode(n.superType))
                this.createGraphWithoutVisualisations(graphComponent, linqset, modelType, nodes)
                break
            case MODEL_TYPE.SYSTEMS:
                nodes = linqset.nodes.filter(n => isSystemInfrastructure(n.superType))
                this.createGraphWithoutVisualisations(graphComponent, linqset, modelType, nodes)
                break
            case MODEL_TYPE.MODELS_CAPTURE_OUTCOMES:
                nodes = linqset.nodes.filter(n => n.superType === NODE_SUPERTYPE.ACTION || (n.superType === NODE_SUPERTYPE.INFORMATION && n.external))
                this.createGraphWithoutVisualisations(graphComponent, linqset, modelType, nodes)
                break
            case MODEL_TYPE.SINGLE_NODE:
                this.createGraphWithoutVisualisations(graphComponent, linqset, modelType, nodes)
                break
            case MODEL_TYPE.OVERVIEW:
                this.createOverview(graphComponent, linqset)
                break
            case MODEL_TYPE.ROLE_CENTRED:
            case MODEL_TYPE.SYSTEM_CENTRED:
                this.singleNodeView(graphComponent, linqset, modelType)
                break
        }
        this.labelService.updateLabelsForGraph(graphComponent.graph, false, linqset, undefined)
        await this.graphService.runLayoutForLinqset(graphComponent, linqset)
        graphComponent.graph?.undoEngine?.clear()
    }

    private createSipoc(organisation:Organisation, linqset:Linqset, graphComponent: GraphComponent) {
        const {graph} = graphComponent
        this.graphService.configureTableEditing(graphComponent)

        const {table, tableNodeStyle} = this.createTable()
        table.rowDefaults.padding = [40, 0, 10, 0]
        table.columnDefaults.padding = [0, 20, 0, 20]

        table.columnDefaults.labels.style = new LabelStyle({
            backgroundFill: '#e0e0e0',
            textFill:'black',
            verticalTextAlignment: 'center',
            horizontalTextAlignment: 'center',
            textSize: 14,
            padding: [5, 5, 5, 5]
        })

        const alternateColumnStyle = new NodeStyleStripeStyleAdapter(
            new ShapeNodeStyle({
                fill: '#abc8e2',
                stroke: 'black',
                shape: 'rectangle'
            })
        )

        const tableGroupNode = graph.createGroupNode(null, table.layout.toRect(), tableNodeStyle)

        const row = table.createRow({
            style: table.rowDefaults.style
        })

        const suppliers = table.createColumn({minWidth:150, style:table.columnDefaults.style})
        table.addLabel(suppliers, 'Suppliers', StretchStripeLabelModel.TOP, table.columnDefaults.labels.style, new Size(200, 30))

        table.addLabel(table.createColumn({minWidth:150, style:alternateColumnStyle}), "Inputs", StretchStripeLabelModel.TOP, table.columnDefaults.labels.style, new Size(200, 30))
        table.addLabel(table.createColumn({minWidth:200, style:table.columnDefaults.style}), "Processes", StretchStripeLabelModel.TOP, table.columnDefaults.labels.style, new Size(200, 30))
        table.addLabel(table.createColumn({minWidth:150, style:alternateColumnStyle}), "Outputs", StretchStripeLabelModel.TOP, table.columnDefaults.labels.style, new Size(200, 30))
        table.addLabel(table.createColumn({minWidth:150, style:table.columnDefaults.style}), "Customers", StretchStripeLabelModel.TOP, table.columnDefaults.labels.style, new Size(200, 30))

        this.createSingleTypeGraph(organisation, linqset, graphComponent, NODE_SUPERTYPE.ACTION)
        const process = table.columns.at(2)
        const input = table.columns.at(1)

        const roleMap = {}
        const actions = graph.nodes.toArray()       // so we dont get a change collection error below
        actions.filter(n => n.tag?.node).forEach(n => {
            const i = getInsights(n.tag.node, linqset)
            if (i?.isInput) {
                ITable.placeNodeInCell(graph, n, tableGroupNode, input, row)
                linqset.edges.filter(e => e.targetKey === n.tag?.node?._key && e.type === EDGE_TYPE.PERFORM).forEach(e => {
                    if (!roleMap[e.sourceKey]) {
                        const role = linqset.getNode(e.sourceKey)
                        if (role) roleMap[e.sourceKey] = this.addNodeToTable(graph, linqset, role, tableGroupNode, table.columns.at(0)!, row)
                    }
                    if (roleMap[e.sourceKey]) this.edgeService.createEdge(graph, roleMap[e.sourceKey], n, e)
                })
            }else {
                ITable.placeNodeInCell(graph, n, tableGroupNode, process, row)
            }
        })

        linqset.nodes.filter(n => n.superType === NODE_SUPERTYPE.INFORMATION).forEach(n => {
            const i = getInsights(n, linqset)
            if (i?.isInput) {
                const info = this.addNodeToTable(graph, linqset, n, tableGroupNode, table.columns.at(1)!, row)
                linqset.edges.filter(e => e.sourceKey === n._key && e.type === EDGE_TYPE.JOINS).forEach(e => {
                    const action = graph.nodes.find(gn => gn.tag?.node?._key === e.targetKey)
                    if (action) this.edgeService.createEdge(graph, info, action, e)
                })
            }else if (i?.isOutput) {
                const output = this.addNodeToTable(graph, linqset, n, tableGroupNode, table.columns.at(3)!, row)
                linqset.edges.filter(e => e.sourceKey === n._key && e.type === EDGE_TYPE.REALISE).forEach(e => {
                    const outcome = linqset.getNode(e.targetKey)
                    if (outcome) this.addNodeToTable(graph, linqset, outcome, tableGroupNode, table.columns.at(4)!, row, output, e)
                })
                linqset.edges.filter(e => e.targetKey === n._key && e.type === EDGE_TYPE.CREATE).forEach(e => {
                    const action = graph.nodes.find(gn => gn.tag?.node?._key === e.sourceKey)
                    if (action) this.edgeService.createEdge(graph, action, output, e)
                })
            }
        })

    }

    private addNodeToTable(graph:IGraph, linqset:Linqset, node:LinqsetNode, tableGroupNode:INode, column:IColumn, row:IRow, sourceNode?:INode, edge?:LinqsetEdge) {
        const iNode = this.nodeService.createNode(linqset, node, '', graph)
        ITable.placeNodeInCell(graph, iNode, tableGroupNode, column, row)

        if (sourceNode && edge) this.edgeService.createEdge(graph, sourceNode, iNode, edge)

        return iNode
    }

    private createTable(): { table: Table, tableNodeStyle: TableNodeStyle } {
        const table = new Table()
        const tableNodeStyle = new TableNodeStyle()

        // set the row defaults

        table.rowDefaults.labels.layoutParameter = StretchStripeLabelModel.LEFT
        table.rowDefaults.minimumSize = 100
        tableNodeStyle.tableRenderingOrder = TableRenderingOrder.ROWS_FIRST
        const backgroundStyle = new ShapeNodeStyle()
        backgroundStyle.fill = Color.ALICE_BLUE
        tableNodeStyle.backgroundStyle = backgroundStyle
        tableNodeStyle.table = table

        table.rowDefaults.style = new NodeStyleStripeStyleAdapter(
            new ShapeNodeStyle({
                fill: '#c4d7ed',
                stroke: 'black',
                shape: 'rectangle'
            })
        )
        return {table, tableNodeStyle}
    }

    private createSwimlaneGraph(linqset:Linqset, graphComponent:GraphComponent, type:NODE_SUPERTYPE) {
        const {graph} = graphComponent
        this.graphService.configureTableEditing(graphComponent)

        const {table, tableNodeStyle} = this.createTable()
        table.rowDefaults.padding = [0, 0, 0, 40]
        table.columnDefaults.padding = [0, 20, 0, 20]

        // The second column style that is used for the even-odd styling of the columns.
        const alternateRowStyle = new NodeStyleStripeStyleAdapter(
            new ShapeNodeStyle({
                fill: '#abc8e2',
                stroke: 'black',
                shape: 'rectangle'
            })
        )

        table.rowDefaults.labels.style = new LabelStyle({
            backgroundFill: '#e0e0e0',
            backgroundStroke: 'black',
            verticalTextAlignment: 'center',
            horizontalTextAlignment: 'center',
            textSize: 14,
            padding: [3, 5, 3, 5]
        })

        table.createColumn()
        const tableGroupNode = graph.createGroupNode(null, table.layout.toRect(), tableNodeStyle)

        const row = table.createRow({
            style: table.rowDefaults.style
        })
        table.addLabel(row, "None", StretchStripeLabelModel.LEFT, table.rowDefaults.labels.style)

        const rows = {}
        rows['none'] = 0

        linqset.nodes.filter(n => n.superType === type).forEach((n, index) => {
            const row = table.createRow({
                style: (index+1) % 2 === 0 ? table.rowDefaults.style : alternateRowStyle,
                tag: n
            })
            table.addLabel(row, n.name, StretchStripeLabelModel.LEFT, table.rowDefaults.labels.style)

            rows[n._key] = index+1
        })

        const actions = graph.nodes.filter(n => n.tag?.node?.superType === NODE_SUPERTYPE.ACTION).toArray()
        actions.forEach(action => {
            const incoming = linqset.edges.filter(e => e.targetKey === action.tag.node._key && (e.type === EDGE_TYPE.AUTOMATE || e.type === EDGE_TYPE.PERFORM))
            const orgNodes = incoming.reduce((a, e) => {
                const orgNode = linqset.getNode(e.sourceKey)
                if (orgNode) {
                    a.push(orgNode)
                    if (orgNode.superType === NODE_SUPERTYPE.ROLE) {
                        // have the role so need to see if using a system
                        const useby = findUsedBy(linqset, e)
                        const system = useby && linqset.getNode(useby.sourceKey)
                        if (system) a.push(system)
                    }
                }
                return a
            }, [] as LinqsetNode[])

            const rowNode = orgNodes.find(on => on.superType === type)
            const orgNode = orgNodes.find(on => on.superType !== type)
            const iOrgNode = orgNode && this.nodeService.createNode(linqset, orgNode, v4(), graph)

            const row = table.rows.at(rows[rowNode?._key] ?? rows['none'])
            const column = table.columns.first()!

            ITable.placeNodeInCell(graph, action, tableGroupNode, column, row)

            if (iOrgNode) {
                ITable.placeNodeInCell(graph, iOrgNode, tableGroupNode, column, row)
                const edgeStyle = this.edgeService.getStyleForEdge(orgNode.superType === NODE_SUPERTYPE.ROLE?EDGE_TYPE.PERFORM:EDGE_TYPE.USEBY)
                graph.createEdge(iOrgNode, action, edgeStyle)
            }

            graph.edgesAt(action, AdjacencyTypes.OUTGOING).toArray().forEach(infoEdge => {
                const infoNode = infoEdge.targetNode.tag?.node

                if (infoNode) {
                    ITable.placeNodeInCell(graph, infoEdge.targetNode, tableGroupNode, column, row)

                    graph.edgesAt(infoEdge.targetNode, AdjacencyTypes.OUTGOING).filter(outcomeEdge => outcomeEdge.tag?.edge && outcomeEdge.tag.edge.type === EDGE_TYPE.REALISE).forEach(outcomeEdge => {
                        const outcomeNode = outcomeEdge.targetNode.tag?.node

                        if (outcomeNode) {
                            ITable.placeNodeInCell(graph, outcomeEdge.targetNode, tableGroupNode, column, row)
                        }
                    })
                }
            })
        })

    }

    private createStandardProcessGraph(graphComponent: GraphComponent, linqset:Linqset, filteredNodes:LinqsetNode[]) {
        const lsNodeMap = new Map<string, {node:LinqsetNode, created:boolean}>();

        const graph = graphComponent.graph

        const nodes = filteredNodes || linqset.nodes

        // place linqset nodes into a map for easy access
        if (nodes) {
            nodes.forEach((node, i) => {
                lsNodeMap.set(node._key, {node:node, created:false})
            })
        }

        if (linqset.edges) {
            const nodeMap = new Map<string, INode>();

            linqset.edges.forEach(edgeData => {
                const originalFromNode = lsNodeMap.get(edgeData.sourceKey)
                const originalToNode = lsNodeMap.get(edgeData.targetKey)

                if (originalFromNode && originalToNode) {
                    let fromNode = this.processEdge(graphComponent, linqset, edgeData, edgeData.sourceVisualisationKey, originalFromNode, nodeMap)
                    let toNode = this.processEdge(graphComponent, linqset, edgeData, edgeData.targetVisualisationKey, originalToNode, nodeMap)

                    if (fromNode && toNode) {
                        this.edgeService.createEdge(graph, fromNode, toNode, edgeData)
                    }
                }
            })
            // include any nodes that do not have edges
            Array.from(lsNodeMap.values()).filter(v => !v.created).forEach(v =>
                this.nodeService.createNode(linqset, v.node, v4(), graph)
            )
        }
    }

    private processEdge(graphComponent: GraphComponent, linqset:Linqset, edge:LinqsetEdge, visualisationKey:string, originalNode:{node:LinqsetNode, created:boolean}, nodeMap:Map<string, INode>) {
        if (isRoleSystem(originalNode.node.superType)) {
            let node = nodeMap.get(visualisationKey)
            if (!node) {
                node = this.createVisualisation(graphComponent, linqset, nodeMap, originalNode, visualisationKey, edge.excluded)
            }
            return node
        }else {
            let node = nodeMap.get(originalNode.node._key)
            if (!node) {
                node = this.createVisualisation(graphComponent, linqset, nodeMap, originalNode, edge.sourceKey, originalNode.node.excluded)
            }
            return node
        }
    }

    private createVisualisation(graphComponent:GraphComponent, linqset:Linqset, nodeMap:Map<string, INode>, originalNode:{node:LinqsetNode, created:boolean}, visualisationKey:string, excluded:boolean) {
        const visualisation = new LinqsetNodeVisualisation(originalNode.node, visualisationKey)
        if (excluded) visualisation.excludeVisualisation()

        originalNode.created = true
        const node = this.nodeService.createNodeForVisualisation(linqset, visualisation, graphComponent.graph)
        nodeMap.set(node.tag.visualisationKey, node)

        if (originalNode.node.external && !linqset.isFilterModel()) {
            if (originalNode.node.modelKey !== linqset.modelKey) this.attachParentModel(graphComponent, node, linqset, nodeMap)
            else this.attachModels(graphComponent, node, linqset, nodeMap)
        }
        return node
    }

    private createOverview(graphComponent: GraphComponent, linqset:Linqset) {
        const nodeMap = new Map<string, INode>();

        const nodes = linqset.nodes.filter(n => {
            const i = getInsights(n, linqset)
            if (linqset.isOrganisationModel()) {
                return ((i.isInput && n.superType===NODE_SUPERTYPE.ACTION) || n.superType === NODE_SUPERTYPE.OUTCOMES)
            }else {
                return (i.isInput || i.isLastNode)
            }
        })
        const modelNode = this.createModelNode(graphComponent, linqset.modelKey, linqset, nodeMap)

        nodes.forEach(n => {
            const node = this.nodeService.createNode(linqset, n, '', graphComponent.graph)
            nodeMap.set(n._key, node)
            const i = getInsights(n, linqset)

            if (i.isInput)
                this.attachJoinedModel(graphComponent, node, linqset.modelKey, linqset, nodeMap)
            else {
                const edgeStyle = this.edgeService.getStyleForEdge(EDGE_TYPE.MODEL)
                graphComponent.graph.createEdge(modelNode, node, edgeStyle)
            }
        })
    }

    private simplifiedModels(graphComponent: GraphComponent, linqset:Linqset) {
        const nodeMap = new Map<string, INode>();

        // create map from each model to a list of models
        const joins = linqset.edges.filter(e => (e.type === EDGE_TYPE.JOINS || e.type === EDGE_TYPE.USED_IN)).reduce((joins, e) => {
            const from = linqset.nodes.find(n => n._key === e.sourceKey)
            if (from) {
                if (!joins[from.modelKey]) joins[from.modelKey] = []
                if (!joins[from.modelKey].find(e2 => e2.modelKey === e.modelKey)) joins[from.modelKey].push(e)
            }
            return joins
        }, {})

        const edgeStyle = this.edgeService.getStyleForEdge(EDGE_TYPE.MODEL)

        // create model nodes first
        linqset.models.forEach(m => {
           this.createModelNode(graphComponent, m._key, linqset, nodeMap)
        })
        Object.keys(joins).forEach(fromKey => {
            const from = graphComponent.graph.nodes.find(n => n.tag?.model?._key === fromKey)
            if (from) {
                joins[fromKey].forEach(e => {
                    const to = graphComponent.graph.nodes.find(n => n.tag?.model?._key === e.modelKey)
                    if (to) {
                        const edge = graphComponent.graph.createEdge(from, to, edgeStyle, createModelEdge(e))
                        graphComponent.graph.addLabel(edge, getEdgeDescription(e.type))
                    }
                })
            }
        })
    }

    private singleNodeView(graphComponent: GraphComponent, linqset:Linqset, modelType:MODEL_TYPE) {
        const {graph} = graphComponent

        if (modelType === MODEL_TYPE.SYSTEM_CENTRED) {
            linqset.nodes.filter(n => n.superType === NODE_SUPERTYPE.SYSTEM).forEach(n => {
                const system = this.nodeService.createNode(linqset, n, '', graph)

                const roleKeys:string[] = []

                linqset.edges.filter(e => e.sourceKey === n._key && (e.type === EDGE_TYPE.AUTOMATE || e.type === EDGE_TYPE.USEBY || e.type === EDGE_TYPE.SUPPLY)).forEach(e => {
                    if (e.type === EDGE_TYPE.USEBY) {
                        if (roleKeys.indexOf(e.targetKey) < 0) {
                            roleKeys.push(e.targetKey)
                            const role = linqset.getNode(e.targetKey)
                            if (role) {
                                const roleINode = this.nodeService.createNode(linqset, role, '', graph)

                                this.edgeService.createEdge(graph, system, roleINode, e)

                                linqset.edges.filter(e2 => e2.sourceKey === role._key && e2.type === EDGE_TYPE.PERFORM).forEach(e2 => {
                                    const action = linqset.getNode(e2.targetKey)
                                    if (action) {
                                        this.edgeService.createEdge(graph, roleINode, this.nodeService.createNode(linqset, action, '', graph), e2)
                                    }
                                })

                            }
                        }
                    }else if (e.type === EDGE_TYPE.AUTOMATE) {
                        const action = linqset.getNode(e.targetKey)
                        if (action) {
                            this.edgeService.createEdge(graph, system, this.nodeService.createNode(linqset, action, '', graph), e)
                        }
                    }else if (e.type === EDGE_TYPE.SUPPLY) {
                        const info = linqset.getNode(e.targetKey)
                        if (info) {
                            this.edgeService.createEdge(graph, system, this.nodeService.createNode(linqset, info, '', graph), e)
                        }
                    }
                })
            })
        }else if (modelType === MODEL_TYPE.ROLE_CENTRED) {
            linqset.nodes.filter(n => n.superType === NODE_SUPERTYPE.ROLE).forEach(n => {
                const role = this.nodeService.createNode(linqset, n, '', graph)

                const systemMap:any = {}

                linqset.edges.filter(e => e.sourceKey === n._key && e.type === EDGE_TYPE.PERFORM).forEach(e => {
                    const action = linqset.getNode(e.targetKey)
                    if (action) {
                        const actionINode = this.nodeService.createNode(linqset, action, '', graph)
                        const useby = findUsedBy(linqset, e)
                        if (useby) {
                            if (!systemMap[useby.sourceKey]) {
                                const system = linqset.getNode(useby.sourceKey)
                                if (system) {
                                    systemMap[useby.sourceKey] = this.nodeService.createNode(linqset, system, '', graph)
                                    this.edgeService.createEdge(graph, role, systemMap[useby.sourceKey], useby)
                                }
                            }
                            if (systemMap[useby.sourceKey]) {
                                this.edgeService.createEdge(graph, systemMap[useby.sourceKey], actionINode, e)
                            }
                        }else {
                            this.edgeService.createEdge(graph, role, actionINode, e)
                        }
                    }
                })
            })
        }
    }

    private createGraphWithoutVisualisations(graphComponent: GraphComponent, linqset:Linqset, modelType:MODEL_TYPE, filteredNodes?:LinqsetNode[]) {
        const nodeMap = new Map<string, INode>();

        const graph = graphComponent.graph

        const nodes = filteredNodes || linqset.nodes

        // create model nodes first
        if (modelType===MODEL_TYPE.MODELS || modelType === MODEL_TYPE.MODELS_CAPTURE_OUTCOMES || modelType === MODEL_TYPE.MODELS_CONNECTION) {
            // we need to add any remaining model nodes.
            const models = linqset.models
            models.forEach(m => {
                if (!nodeMap.has(m._key)) {
                    this.createModelNode(graphComponent, m._key, linqset, nodeMap)
                }
            })
        }

        // place linqset nodes into a map for easy access
        nodes.forEach(lsNode => {
            if (lsNode._key && !nodeMap.has(lsNode._key)) {

                const exclude = linqset.isOrganisationModel() && isRoleSystem(lsNode.superType) && (!lsNode.insights || !lsNode.insights.find(i => i.modelKey !== ORGANISATION_MODEL_KEY))

                const node = this.nodeService.createNode(linqset, lsNode, '', graph, exclude)

                nodeMap.set(lsNode._key, node)
                // connector node from a different model so show the model it comes from
                if (lsNode.external) {
                    if (lsNode.modelKey === linqset.modelKey) {
                        this.attachModels(graphComponent, node, linqset, nodeMap)
                    }else {
                        this.attachParentModel(graphComponent, node, linqset, nodeMap)
                    }
                }else if (!linqset.hasModelNodes) {
                    if (lsNode.type === NODE_TYPE.CAPTURE)
                        this.attachJoinedModel(graphComponent, node, lsNode.modelKey, linqset, nodeMap)
                    else if (lsNode.superType === NODE_SUPERTYPE.OUTCOMES)
                        this.attachParentModel(graphComponent, node, linqset, nodeMap)
                }
            }
        })

        const edges = modelType===MODEL_TYPE.MODELS?linqset.edges.filter(e => (e.type === EDGE_TYPE.JOINS  || e.type === EDGE_TYPE.USED_IN)):
                    (modelType === MODEL_TYPE.PEOPLE_AND_SYSTEMS?linqset.edges.filter(e => isOrganisationEdge(e.type) || e.type === EDGE_TYPE.USEBY):
                    linqset.edges)

        // one edge for each pair of nodes
        const processed = new Set<string>()

        edges.forEach(edgeData => {
            const pair = edgeData.sourceKey + '|' + edgeData.targetKey
            if (!processed.has(pair)) {
                processed.add(pair)
                // reset these as will be setup from scratch below
                const fromNode = nodeMap.get(edgeData.sourceKey)
                const toNode = nodeMap.get(edgeData.targetKey)

                if (fromNode && toNode) {
                    const edge = this.edgeService.createEdge(graph, fromNode, toNode, edgeData)
                    if ((edgeData.type === EDGE_TYPE.JOINS  || edgeData.type === EDGE_TYPE.USED_IN) && !linqset.hasModelNodes) {
                        // join between models and so show the joined to model
                        this.attachJoinedModel(graphComponent, toNode, edge.tag.edge.modelKey, linqset, nodeMap)
                    }
                }
            }
        })
    }

    private createModelNode(graphComponent: GraphComponent, modelKey:string, linqset:Linqset, nodeMap:Map<string, INode>, highlighted:boolean = false) {
        // we need to check whether we have a new model created when moving nodes
        const model = linqset.changes?.newModel && linqset.changes.newModel._key === modelKey?linqset.changes.newModel:(modelKey===ORGANISATION_MODEL_KEY || modelKey===FILTER_MODEL_KEY?linqset.organisation?.model:linqset.organisation?.models?.find(m => m._key === modelKey))
        if (model) {
            if (nodeMap) {
                if (!nodeMap.has(modelKey)) {
                    nodeMap.set(modelKey, this.nodeService.createModelNode(model, linqset.workspaceKey, graphComponent))
                }
                return nodeMap.get(modelKey)
            }else {
                return this.nodeService.createModelNode(model, linqset.workspaceKey, graphComponent)
            }
        }
        return undefined
    }

    private attachModels(graphComponent: GraphComponent, node:INode, linqset:Linqset, nodeMap?:Map<string, INode>) {
        if (this.repoService.organisationLinqset) {
            this.repoService.organisationLinqset.edges.filter(e => e.sourceKey === node.tag.node._key && (e.type === EDGE_TYPE.JOINS || e.type === EDGE_TYPE.USED_IN)).forEach(j => {
                this.attachJoinedModel(graphComponent, node, j.modelKey, linqset, nodeMap)
            })
        }
    }

    private attachParentModel(graphComponent: GraphComponent, node:INode, linqset:Linqset, nodeMap?:Map<string, INode>) {
        const modelNode = this.createModelNode(graphComponent, node.tag.node.modelKey, linqset, nodeMap)
        if (!modelNode) return

        const edgeStyle = this.edgeService.getStyleForEdge(EDGE_TYPE.MODEL)
        graphComponent.graph.createEdge(modelNode, node, edgeStyle)
    }

    private attachJoinedModel(graphComponent: GraphComponent, node:INode, modelKey:string, linqset:Linqset, nodeMap?:Map<string, INode>) {
        const modelNode = this.createModelNode(graphComponent, modelKey, linqset, nodeMap)

        if (modelNode) {
            const edges = graphComponent.graph.edgesAt(modelNode, AdjacencyTypes.INCOMING)
            if (edges.find(e => e.sourceNode === node)) return

            const edgeStyle = this.edgeService.getStyleForEdge(EDGE_TYPE.MODEL)
            graphComponent.graph.createEdge(node, modelNode, edgeStyle)
        }else {
            console.error(`Cannot find model with key ${modelKey} for this node.`)
        }
    }

    // follows the process on from an action to the next actions another role performs: the role hands over to them.
    // Actions already visited are skipped, so a loop in the process ends
    private traverseSupplyChain = (linqset:Linqset, graphComponent:GraphComponent, iNodeMap:any, actionKey:string, role:LinqsetNode, visited = new Set<string>()) => {
        if (visited.has(actionKey)) return
        visited.add(actionKey)

        linqset.edges.filter(e => e.sourceKey === actionKey && e.type == EDGE_TYPE.CREATE).forEach(e => {
            // action creates info
            linqset.edges.filter(e2 => e2.sourceKey === e.targetKey && e2.type == EDGE_TYPE.SUPPORT).forEach(e2 => {
                // info supports action
                const otherRoles = linqset.edges.filter(e3 => e3.targetKey === e2.targetKey && e3.sourceKey !== role._key && e3.type == EDGE_TYPE.PERFORM)
                // roles perform actions. if find a role that isn't starting role we end
                otherRoles.forEach(e3 => this.createInterfaceNode(linqset, graphComponent, iNodeMap, iNodeMap[role._key], e3.sourceKey, e2))
                // didnt find a role to connect to, so continue down chain
                if (otherRoles.length === 0) this.traverseSupplyChain(linqset, graphComponent, iNodeMap, e2.targetKey, role, visited)
            })
        })
    }

    private createRoleInterfacesGraph = (linqset:Linqset, graphComponent:GraphComponent) => {
        const iNodeMap = {}

        linqset.nodes.filter(n => n.superType === NODE_SUPERTYPE.ROLE).forEach(r => {
            if (!iNodeMap[r._key]) {
                iNodeMap[r._key] = this.nodeService.createNode(linqset, r, r._key, graphComponent.graph)
            }
            // go through each action node that is performed
            linqset.edges.filter(e => e.sourceKey === r._key && e.type == EDGE_TYPE.PERFORM).forEach(e => {
                // Role performs action
                this.traverseSupplyChain(linqset, graphComponent, iNodeMap, e.targetKey, r)
            })
        })
    }

    private createSystemInterfacesGraph = (linqset:Linqset, graphComponent:GraphComponent) => {
        const iNodeMap = {}

        linqset.nodes.filter(n => n.superType === NODE_SUPERTYPE.SYSTEM).forEach(s => {
            if (!iNodeMap[s._key]) {
                iNodeMap[s._key] = this.nodeService.createNode(linqset, s, s._key, graphComponent.graph)
            }
            // go through each info node that is supplied and go to each action after that and attach to any systems executing that action
            linqset.edges.filter(e => e.sourceKey === s._key && e.type == EDGE_TYPE.SUPPLY).forEach(e => {
                // system supplies info
                linqset.edges.filter(e2 => e2.sourceKey === e.targetKey && e2.type == EDGE_TYPE.SUPPORT).forEach(e2 => {
                    // info support action
                    linqset.edges.filter(e3 => e3.targetKey === e2.targetKey && e3.sourceKey !== s._key && e3.type == EDGE_TYPE.AUTOMATE).forEach(e3 => {
                        // any systems (other than starting system) that automate this action
                        // create inode for that system
                        this.connectSystems(linqset, graphComponent, iNodeMap, s._key, e.targetKey, e3.sourceKey)
                    })
                    // check roles
                    linqset.edges.filter(e3 => e3.targetKey === e2.targetKey && e3.type == EDGE_TYPE.PERFORM).forEach(e3 => {
                        linqset.edges.filter(e4 => e4.targetVisualisationKey === e3.sourceVisualisationKey && e4.sourceKey !== s._key && e4.type == EDGE_TYPE.USEBY).forEach(e4 => {
                            this.connectSystems(linqset, graphComponent, iNodeMap, s._key, e.targetKey, e4.sourceKey)
                        })
                    })
                })
            })
        })
    }

    private connectSystems = (linqset:Linqset, graphComponent:GraphComponent, iNodeMap:any, systemNodeKey:string, infoNodeKey:string, connectedSystemNodeKey:string) => {
        const supplyStyle = this.edgeService.getStyleForEdge(EDGE_TYPE.SUPPLY)
        const supportStyle = this.edgeService.getStyleForEdge(EDGE_TYPE.SUPPORT)

        // create info node
        if (!iNodeMap[infoNodeKey]) {
            // info node has not been created
            const info = linqset.getNode(infoNodeKey)
            if (info) {
                if (info.informationAssetKey && info.informationAssetKey.length > 0) {
                    if (iNodeMap[info.informationAssetKey]) {
                        infoNodeKey = info.informationAssetKey
                    }else {
                        // info refers to asset so replace it with that
                        const asset = this.organisationService.organisation?.informationAssets?.find(ia => ia._key === info.informationAssetKey)
                        if (asset) {
                            infoNodeKey = asset._key
                            iNodeMap[infoNodeKey] = this.nodeService.createAssetNode(asset, graphComponent)
                        }
                    }
                }

                if (infoNodeKey === info._key) {
                    // still using info node so create that
                    iNodeMap[info._key] = this.nodeService.createNode(linqset, info, info._key, graphComponent.graph)
                }
            }
        }
        if (iNodeMap[infoNodeKey]) {
            // create edge from system to info or asset node
            if (!graphComponent.graph.edgesAt(iNodeMap[systemNodeKey], AdjacencyTypes.OUTGOING).find(ea => ea.targetNode === iNodeMap[infoNodeKey])) {
                // not found so create it
                graphComponent.graph.createEdge(iNodeMap[systemNodeKey], iNodeMap[infoNodeKey], supplyStyle)
            }

            // check to see if connected system is there, if not create it
            if (!iNodeMap[connectedSystemNodeKey]) {
                const connectedSystem = linqset.getNode(connectedSystemNodeKey)
                if (connectedSystem) iNodeMap[connectedSystemNodeKey] = this.nodeService.createNode(linqset, connectedSystem, connectedSystem._key, graphComponent.graph)
            }
            if (iNodeMap[connectedSystemNodeKey]) {
                // connect info to connected system
                let existing = graphComponent.graph.edgesAt(iNodeMap[infoNodeKey], AdjacencyTypes.OUTGOING).find((ie:IEdge) => ie.targetNode === iNodeMap[connectedSystemNodeKey])
                if (!existing) {
                    existing = graphComponent.graph.createEdge(iNodeMap[infoNodeKey], iNodeMap[connectedSystemNodeKey], supportStyle)
                }
            }
        }
    }

    private createInterfaceNode = (linqset:Linqset, graphComponent:GraphComponent, iNodeMap:any, startingNode:INode, nodeKey:string, edge:LinqsetEdge) => {
        if (!iNodeMap[nodeKey]) {
            const s4 = linqset.getNode(nodeKey)
            if (!s4) return
            iNodeMap[nodeKey] = this.nodeService.createNode(linqset, s4, s4._key, graphComponent.graph)
        }
        let newEdge = graphComponent.graph.edgesAt(startingNode, AdjacencyTypes.OUTGOING).find((ie:IEdge) => ie.targetNode === iNodeMap[nodeKey])
        if (!newEdge) {
            newEdge = this.edgeService.createEdge(graphComponent.graph, startingNode, iNodeMap[nodeKey], edge)
        }
        const start = linqset.getNode(edge.sourceKey)
        const end = linqset.getNode(edge.targetKey)
        if (start && !newEdge.tag.nodes.find(n => n._key === start._key)) newEdge.tag.nodes.push(start)
        if (end && !newEdge.tag.nodes.find(n => n._key === end._key)) newEdge.tag.nodes.push(end)
    }

    private createInformationAssetGraph = (organisation:Organisation, linqset:Linqset, graphComponent:GraphComponent) => {
        const keysProcessed = new Set<string>()

        const startingNodes = getStartingNodes(linqset.nodes, linqset.edges)
        startingNodes.forEach((n:LinqsetNode) => {
            if (n.superType !== NODE_SUPERTYPE.INFORMATION) {
                linqset.getEdgesFromNode(n).forEach(ie => {
                    this.createInformationAsset(organisation, linqset, graphComponent, keysProcessed, linqset.getNode(ie.targetKey))
                })
            }else this.createInformationAsset(organisation, linqset, graphComponent, keysProcessed, n)
        })
    }

    private createInformationAsset = (organisation:Organisation, linqset:Linqset, graphComponent:GraphComponent, keysProcessed:Set<string>, info:LinqsetNode, currentNode?:INode) => {
        // an edge can point at a node the model no longer has
        if (!info || keysProcessed.has(info._key)) return
        keysProcessed.add(info._key)

        let node:INode = undefined

        const edgeStyle = this.edgeService.getStyleForEdge(EDGE_TYPE.SUPPORT)
        if (info.informationAssetKey){
            if (currentNode?.tag?.asset && info.informationAssetKey === currentNode.tag.asset._key) {
                // same asset
                node = currentNode
            }else {
                // change in asset
                const asset = organisation?.informationAssets?.find(ia => ia._key === info.informationAssetKey)

                if (asset) {
                    node = this.nodeService.createAssetNode(asset, graphComponent)
                    if (currentNode) graphComponent.graph.createEdge(currentNode, node, edgeStyle)
                    this.attachSystem(linqset, info, node, graphComponent)
                }
            }
        }
        if (!node) {
            // if no asset use info node instead
            node = this.nodeService.createNode(linqset, info, info._key, graphComponent.graph)
            if (currentNode) graphComponent.graph.createEdge(currentNode, node, edgeStyle)
            this.attachSystem(linqset, info, node, graphComponent)
        }

        // process each subsequent node
        linqset.getEdgesFromNode(info).forEach(e => {
            const action = linqset.getNode(e.targetKey)
            if (action) {
                linqset.getEdgesFromNode(action).forEach(ae => {
                    this.createInformationAsset(organisation, linqset, graphComponent, keysProcessed, linqset.getNode(ae.targetKey), node)
                })
            }
        })

    }

    private attachSystem = (linqset:Linqset, infoNode:LinqsetNode, node:INode, graphComponent:GraphComponent) => {
        // node is either asset or info
        const systemEdge = linqset.edges.find(e => e.targetKey === infoNode._key && e.type === EDGE_TYPE.SUPPLY)
        if (systemEdge) {
            const system = linqset.getNode(systemEdge.sourceKey)
            if (system) {
                const visualisation = new LinqsetNodeVisualisation(system, systemEdge.sourceVisualisationKey)
                const systemNode = this.nodeService.createNodeForVisualisation(linqset, visualisation, graphComponent.graph)
                const edgeStyle = this.edgeService.getStyleForEdge(systemEdge.type)
                graphComponent.graph.createEdge(systemNode, node, edgeStyle)
            }
        }

    }

    private createSingleTypeGraph = (organisation:Organisation, linqset:Linqset, graphComponent:GraphComponent, type:NODE_SUPERTYPE) => {
        const iNodeMap = {}

        const startingNodes = getStartingNodes(linqset.nodes, linqset.edges)
        startingNodes.forEach((n:LinqsetNode) => {
            if (n.superType !== type) {
                linqset.getEdgesFromNode(n).forEach(ie => {
                    this.createNodeInGraph(organisation, linqset, linqset.getNode(ie.targetKey), graphComponent, type, iNodeMap)
                })
            }else this.createNodeInGraph(organisation, linqset, n, graphComponent, type, iNodeMap)
        })

    }

    private createNodeInGraph = (organisation:Organisation, linqset:Linqset, n:LinqsetNode, graphComponent:GraphComponent, type:NODE_SUPERTYPE, iNodeMap:any, previousAction?:INode, nodeName?:string) => {
        if (n?.superType === type) {
            let newNode = false
            if (!iNodeMap[n._key]) {
                iNodeMap[n._key] = this.nodeService.createNode(linqset, n, n._key, graphComponent.graph)
                newNode = true

                if (type === NODE_SUPERTYPE.INFORMATION) {
                    if (n.informationAssetKey) {
                        if (!iNodeMap[n.informationAssetKey]) {
                            const asset = organisation?.informationAssets?.find(ia => ia._key === n.informationAssetKey)
                            if (asset) iNodeMap[n.informationAssetKey] = this.nodeService.createAssetNode(asset, graphComponent)
                        }

                        if (iNodeMap[n.informationAssetKey]) {
                            const edgeStyle = this.edgeService.getStyleForEdge(EDGE_TYPE.INFO_ASSET)
                            graphComponent.graph.createEdge(iNodeMap[n._key], iNodeMap[n.informationAssetKey], edgeStyle)
                        }
                    }
                    this.attachSystem(linqset, n, iNodeMap[n._key], graphComponent)
                }
            }

            if (previousAction) {
                const edge = this.edgeService.createNewEdge(graphComponent.graph, linqset, previousAction, iNodeMap[n._key])
                edge.tag.label = nodeName
            }
            if (newNode) {
                // process each subsequent node
                linqset.getEdgesFromNode(n).forEach(e => {
                    const info = linqset.getNode(e.targetKey)
                    if (info) {
                        linqset.getEdgesFromNode(info).forEach(ie => {
                            this.createNodeInGraph(organisation, linqset, linqset.getNode(ie.targetKey), graphComponent, type, iNodeMap, iNodeMap[n._key], info.name)
                        })
                    }
                })
            }
        }
    }

    public createMiniGraph(linqset:Linqset, miniGraphComponent:GraphComponent, edges:IEdge[], selectedNodes:INode[]) {
        const nodeMap = new Map<string, INode>();

        edges.forEach(e => {
            try {
                const sourceNode = this.createMiniGraphNode(linqset, miniGraphComponent, nodeMap, e.sourceNode, selectedNodes)
                const targetNode = this.createMiniGraphNode(linqset, miniGraphComponent, nodeMap, e.targetNode, selectedNodes)
                if (e.tag && e.tag.edge) {
                    this.edgeService.createEdge(miniGraphComponent.graph, sourceNode, targetNode, e.tag.edge)
                }else {
                    const edgeStyle = this.edgeService.getStyleForEdge(EDGE_TYPE.MODEL)
                    miniGraphComponent.graph.createEdge(sourceNode, targetNode, edgeStyle)
                }
            }catch(err){}
        })

        // selected nodes with nothing connected in this view still appear
        selectedNodes?.forEach(n => {
            try {
                if (n.tag?.node || n.tag?.model) this.createMiniGraphNode(linqset, miniGraphComponent, nodeMap, n, selectedNodes)
            }catch(err){}
        })
    }

    private createMiniGraphNode(linqset:Linqset, miniGraphComponent:GraphComponent, nodeMap:Map<string, INode>, node:INode, selectedNodes:INode[]) {
        let newNode:INode
        const isSelected = selectedNodes && selectedNodes.find(n => n===node)?true:false

        if (node.tag.node) {
            const linqsetNode = node.tag.node
            newNode = nodeMap.get(linqsetNode._key)
            if (!newNode) {
                newNode = this.nodeService.createNode(linqset, linqsetNode, node.tag.visualisationKey, miniGraphComponent.graph, false)
                nodeMap.set(linqsetNode._key, newNode)
            }
        }else if (node.tag.model) {
            // is a model node
            newNode = this.createModelNode(miniGraphComponent, node.tag.model._key, linqset, nodeMap)
        }
        if (isSelected) {
            miniGraphComponent.currentItem = newNode
            miniGraphComponent.selection.add(newNode)
        }

        return newNode
    }
}